import { buildSheet, normalizeHeader } from './productImport';
import {
  cellText,
  compactLine,
  existingReason,
  hashText,
  importRefFor,
  isBlank,
  line as text,
  multiline,
  norm,
  projectMatcher,
  proposalTotals,
  readBool,
  readDate,
  readNumber,
  readRow,
  round2,
} from './recordImport';

// Proposals come from two kinds of file:
//  - Ivy's account export, read as it is: each proposal is an Invoices row of
//    type Proposal, and its lines are the Invoice Items rows with its Invoice
//    Number.
//  - A sheet with one row per proposal line (Houzz Pro, Ivy's project
//    tracker, Design Manager, Programa, Studio Designer, any spreadsheet),
//    mapped like other record sheets and grouped into proposals.
// Both end up as review rows with the lines ready to save.

/* ============================================================
   Pricing a line
============================================================ */

const markupText = (value) => Number(value.toFixed(8));

// Cost, markup and client price for one line, kept the way the proposal editor
// reads them: it shows cost plus markup as the client price, so when the
// client price is known the markup is worked out from it, and a line with no
// known cost costs its client price. A credit (a negative price) becomes a
// negative quantity at a positive price, which is how proposals hold one.
export function priceLine({ qty = 1, cost = null, markup = null, clientUnit = null }) {
  let quantity = qty;
  let unitCost = cost;
  let unitPrice = clientUnit;
  if ((unitPrice ?? unitCost ?? 0) < 0) {
    quantity = -quantity;
    unitPrice = unitPrice === null ? null : -unitPrice;
    unitCost = unitCost === null ? null : Math.abs(unitCost);
  }
  if (unitPrice === null) {
    if (unitCost === null) return { qty: quantity, tradePrice: 0, markup: 0, clientUnit: 0 };
    const m = markup ?? 0;
    return {
      qty: quantity,
      tradePrice: unitCost,
      markup: m,
      clientUnit: Math.max(round2(unitCost + unitCost * (m / 100)), 0),
    };
  }
  const price = round2(unitPrice);
  const tradePrice = unitCost !== null && unitCost > 0 ? unitCost : price;
  return {
    qty: quantity,
    tradePrice,
    markup: tradePrice > 0 ? markupText((price / tradePrice - 1) * 100) : 0,
    clientUnit: price,
  };
}

const clientShipping = (shipping, markup) => round2(shipping + shipping * (markup / 100));

/* ============================================================
   Ivy's account export
============================================================ */

const IVY_INVOICES = 'invoices';
const IVY_ITEMS = 'invoice items';

const IVY_HEADERS = {
  invoices: ['invoice number', 'invoice type', 'code', 'project name', 'total payment'],
  items: ['invoice number', 'title', 'quantity', 'rate', 'markup'],
  projects: ['project name', 'prefix', 'client name'],
  library: ['title', 'taxable'],
};

const sectionFor = (sheets, name) =>
  sheets.find((sheet) => sheet.section && normalizeHeader(sheet.name) === name) || null;

function buildSection(sheet, headers) {
  if (!sheet) return null;
  const built = buildSheet(sheet.name, sheet.rows, {
    knownHeaders: new Set(headers),
    keepSingleCellRows: true,
    rowNumbers: sheet.rowNumbers,
  });
  const index = new Map(built.headers.map((header, col) => [normalizeHeader(header), col]));
  // A cell by column name; '' when the section has no such column.
  const get = (row, header) => (index.has(header) ? row.cells[index.get(header)] : '');
  return { ...built, get, date1904: Boolean(sheet.date1904) };
}

// The sections of an Ivy export that proposals are read from, or null when the
// file isn't one.
export function ivyProposalSource(sheets = []) {
  const invoices = sectionFor(sheets, IVY_INVOICES);
  const items = sectionFor(sheets, IVY_ITEMS);
  if (!invoices || !items) return null;
  return {
    invoices: buildSection(invoices, IVY_HEADERS.invoices),
    items: buildSection(items, IVY_HEADERS.items),
    projects: buildSection(sectionFor(sheets, 'projects'), IVY_HEADERS.projects),
    products: buildSection(sectionFor(sheets, 'products'), IVY_HEADERS.library),
    services: buildSection(sectionFor(sheets, 'services'), IVY_HEADERS.library),
  };
}

const SHIPPING_MODES = ['none', 'all', 'taxable'];

// Ivy's export has each proposal's tax but not which of its lines were taxed.
// Lines start out taxable as their library item is; when that doesn't come to
// Ivy's tax, the first of these that does is used: shipping taxed or not, then
// one line switched, then two. If nothing does, the library flags stay and the
// review shows that the total differs. `targetTax` is null when unknown.
export function fitTaxFlags(lines, rate, targetTax) {
  if (!rate || targetTax === null) return lines.map((l) => ({ ...l, shippingTaxable: false }));
  if (!targetTax) return lines.map((l) => ({ ...l, taxable: false, shippingTaxable: false }));

  const merch = lines.map((l) => round2(l.clientUnit * l.qty));
  const ship = lines.map((l) => (l.qty > 0 ? l.clientShipping : 0));
  const start = lines.map((l) => Boolean(l.taxable));
  const shipTaxed = (mode, taxed) => mode === 'all' || (mode === 'taxable' && taxed);
  const amount = (i, taxed, mode) =>
    (taxed ? merch[i] : 0) + (shipTaxed(mode, taxed) ? ship[i] : 0);
  const matches = (base) => Math.abs(round2(round2(base) * rate) - targetTax) <= 0.02;

  const found = (() => {
    const bases = SHIPPING_MODES.map((mode) =>
      start.reduce((sum, taxed, i) => sum + amount(i, taxed, mode), 0)
    );
    const deltas = SHIPPING_MODES.map((mode) =>
      start.map((taxed, i) => amount(i, !taxed, mode) - amount(i, taxed, mode))
    );
    for (let m = 0; m < SHIPPING_MODES.length; m += 1) {
      if (matches(bases[m])) return { mode: SHIPPING_MODES[m], flips: [] };
    }
    for (let m = 0; m < SHIPPING_MODES.length; m += 1) {
      for (let a = 0; a < lines.length; a += 1) {
        if (matches(bases[m] + deltas[m][a])) return { mode: SHIPPING_MODES[m], flips: [a] };
      }
    }
    if (lines.length > 200) return null;
    for (let m = 0; m < SHIPPING_MODES.length; m += 1) {
      for (let a = 0; a < lines.length; a += 1) {
        for (let b = a + 1; b < lines.length; b += 1) {
          if (matches(bases[m] + deltas[m][a] + deltas[m][b])) {
            return { mode: SHIPPING_MODES[m], flips: [a, b] };
          }
        }
      }
    }
    return null;
  })();

  const { mode, flips } = found || { mode: 'none', flips: [] };
  return lines.map((l, i) => {
    const taxed = flips.includes(i) ? !start[i] : start[i];
    return { ...l, taxable: taxed, shippingTaxable: l.qty > 0 && shipTaxed(mode, taxed) };
  });
}

// Library titles: taxable as the item is in Ivy (products are unless marked
// otherwise, services aren't unless marked), and which are services.
function ivyLibrary(source) {
  const taxable = new Map();
  const services = new Set();
  const add = (section, isService) => {
    section?.dataRows.forEach((row) => {
      const key = norm(section.get(row, 'title'));
      if (!key) return;
      const flag = readBool(section.get(row, 'taxable'));
      taxable.set(key, isService ? flag === true : flag !== false);
      if (isService) services.add(key);
    });
  };
  add(source.products, false);
  add(source.services, true);
  return { taxable, services };
}

function ivyLine(items, row, library, deposit) {
  const name = text(items.get(row, 'title'));
  const key = norm(name);
  const priced = priceLine({
    qty: readNumber(items.get(row, 'quantity')) ?? 1,
    cost: readNumber(items.get(row, 'rate')) ?? 0,
    markup: readNumber(items.get(row, 'markup')) ?? 0,
  });
  const shipping = readNumber(items.get(row, 'shipping cost')) ?? 0;
  const shippingMarkup = readNumber(items.get(row, 'shipping markup')) ?? 0;
  return {
    name,
    description: multiline(items.get(row, 'description')),
    ...priced,
    shipping,
    shippingMarkup,
    clientShipping: clientShipping(shipping, shippingMarkup),
    msrp: readNumber(items.get(row, 'msrp')) ?? 0,
    type: library.services.has(key) ? 'service' : 'product',
    taxable: library.taxable.get(key) ?? true,
    shippingTaxable: false,
    deposit,
  };
}

const payloadSize = (lines) => JSON.stringify(lines.map(compactLine)).length;

// The proposals in an Ivy export, with counts of what was left out. Invoices
// and retainers share the Invoices section and aren't proposals.
export function ivyProposalEntries(source) {
  const { invoices, items, projects } = source;
  const meta = { date1904: invoices.date1904 };
  const stats = {
    lines: 0,
    removedProposals: 0,
    removedLines: 0,
    emptyProposals: 0,
    otherDocuments: {},
  };

  // Lines by invoice number; removed ones are only counted.
  const itemsByInvoice = new Map();
  const removedByInvoice = new Map();
  items.dataRows.forEach((row) => {
    const key = cellText(items.get(row, 'invoice number')).trim();
    if (!isBlank(items.get(row, 'removed date'))) {
      removedByInvoice.set(key, (removedByInvoice.get(key) || 0) + 1);
      return;
    }
    if (!itemsByInvoice.has(key)) itemsByInvoice.set(key, []);
    itemsByInvoice.get(key).push(row);
  });

  const prefixes = new Map();
  projects?.dataRows.forEach((row) => {
    const prefix = text(projects.get(row, 'prefix'));
    if (prefix) prefixes.set(norm(projects.get(row, 'project name')), prefix);
  });
  const library = ivyLibrary(source);

  const entries = [];
  invoices.dataRows.forEach((row) => {
    const type = text(invoices.get(row, 'invoice type')) || 'Other';
    if (norm(type) !== 'proposal') {
      stats.otherDocuments[type] = (stats.otherDocuments[type] || 0) + 1;
      return;
    }
    if (!isBlank(invoices.get(row, 'removed date'))) {
      stats.removedProposals += 1;
      return;
    }
    const invoiceNumber = cellText(invoices.get(row, 'invoice number')).trim();
    const itemRows = itemsByInvoice.get(invoiceNumber) || [];
    if (!itemRows.length) {
      stats.emptyProposals += 1;
      return;
    }
    stats.removedLines += removedByInvoice.get(invoiceNumber) || 0;

    const projectName = text(invoices.get(row, 'project name'));
    const code = text(invoices.get(row, 'code'));
    const prefix = prefixes.get(norm(projectName));
    const rate = (readNumber(invoices.get(row, 'sales tax amount')) ?? 0) / 100;
    const deposit = readNumber(invoices.get(row, 'deposit percentage value')) ?? 0;
    const lines = fitTaxFlags(
      itemRows.map((itemRow) => ivyLine(items, itemRow, library, deposit)),
      rate,
      readNumber(invoices.get(row, 'taxes derived'))
    );
    stats.lines += lines.length;

    entries.push({
      importRef: importRefFor(invoices.name, row),
      sourceRows: [row.rowNumber],
      fields: {
        proposalNumber: code ? `PR-${prefix ? `${prefix}-` : ''}${code}` : '',
        projectId: '',
        date: readDate(invoices.get(row, 'invoice date'), meta)?.date || '',
        title: '',
        status: 'archived',
      },
      extra: {
        projectName,
        clientName: text(invoices.get(row, 'client name')),
        clientEmail: text(invoices.get(row, 'client email')),
        billingAddress: text(invoices.get(row, 'client billing address')),
        notes: multiline(invoices.get(row, 'notes')),
        createdAt: readDate(invoices.get(row, 'created at'), meta)?.iso || '',
        salesTaxRate: rate,
        lines,
        total: proposalTotals(lines, rate).total,
        sourceTotal: readNumber(invoices.get(row, 'total payment')),
        unpricedLines: 0,
        payloadBytes: payloadSize(lines),
      },
    });
  });
  return { entries, stats: { ...stats, proposals: entries.length } };
}

/* ============================================================
   A sheet of proposal lines
============================================================ */

const SELLING_FIELDS = ['unitPrice', 'totalPrice'];

// "Proposal: ES-10028", "Estimate #12", or a number with a proposal prefix
// (Ivy's PR-11261, Houzz's ES-10028).
const PROPOSAL_REF = [
  /\b(?:proposal|estimate)\b\s*(?:#|no\.?|number)?\s*[:#-]?\s*([A-Za-z0-9-]*\d[\w./-]*)/i,
  /\b((?:PR|ES|EST|P)-[A-Za-z0-9-]*\d[\w./-]*)/i,
];
// "PO: PO-3301", "IN-00005", "Invoice 1042": the row is on an order or invoice.
const OTHER_DOCUMENT = /\b(?:po|purchase order|invoice|inv|in)\b[\s:#-]*[A-Za-z0-9-]*\d/i;
// Progress columns sometimes hold a stage rather than a document.
const STAGE = /\b(?:sent|approved|declined|rejected|ordered|invoiced|paid|draft|pending)\b/i;

// What a proposal column says about its row: the proposal it's on, or that
// it's only on a purchase order or invoice. A cell that's neither a reference
// nor a stage is the proposal's name.
export function readProposalKey(value) {
  const cell = text(value);
  if (!cell) return { key: '', notOnProposal: false };
  for (const pattern of PROPOSAL_REF) {
    const found = cell.match(pattern);
    if (found) return { key: found[1], notOnProposal: false };
  }
  if (OTHER_DOCUMENT.test(cell)) return { key: '', notOnProposal: true };
  if (!/\d/.test(cell) && STAGE.test(cell)) return { key: '', notOnProposal: false };
  return { key: cell, notOnProposal: false };
}

// A code ("ES-10028", "1042") is kept as the proposal number; a name ("Living
// Room Furniture") becomes the title, and the studio's next number is used.
const looksLikeNumber = (key) => /\d/.test(key) && !/\s/.test(key) && key.length <= 30;

const DECLINED = /\b(declined|rejected|not approved)\b/i;

// Markup columns hold 35 for 35%, unless every value is a fraction (0.35).
function percentScale(entries, key) {
  const values = entries.map((e) => e.values[key]).filter((v) => typeof v === 'number' && v);
  return values.length && values.every((v) => Math.abs(v) <= 1) ? 100 : 1;
}

// Whether "Unit Selling Price" is before markup, as Houzz Pro and Ivy trackers
// export it (Total Selling Price = Quantity x Unit Selling Price x (1 +
// Markup %)), judged from rows that have all four. null when none do.
export function detectSellMode(sheet, mapping) {
  if (!mapping.includes('unitPrice') || !mapping.includes('markup')) return null;
  if (!mapping.includes('totalPrice')) return null;
  const entries = sheet.dataRows.map((row) => readRow('proposals', row, mapping, sheet));
  const scale = percentScale(entries, 'markup');
  let before = 0;
  let after = 0;
  entries.forEach(({ values: v }) => {
    const qty = v.qty ?? 1;
    if (!v.unitPrice || !v.totalPrice || !v.markup) return;
    const withMarkup = qty * v.unitPrice * (1 + (v.markup * scale) / 100);
    if (Math.abs(withMarkup - v.totalPrice) < 0.05) before += 1;
    else if (Math.abs(qty * v.unitPrice - v.totalPrice) < 0.05) after += 1;
  });
  if (!before && !after) return null;
  return before >= after ? 'withMarkup' : 'file';
}

function sheetLine(v, { sellMode, markupScale, shippingScale }) {
  const qty = v.qty ?? 1;
  const markup = v.markup === null || v.markup === undefined ? null : v.markup * markupScale;
  const cost = v.unitCost ?? null;
  let input;
  if (v.unitPrice) {
    if (sellMode === 'withMarkup' && markup !== null) {
      const price = v.unitPrice + v.unitPrice * (markup / 100);
      input =
        cost !== null && cost > 0 ? { cost, clientUnit: price } : { cost: v.unitPrice, markup };
    } else {
      input = { cost, clientUnit: v.unitPrice };
    }
  } else if (v.totalPrice && qty) {
    input = { cost, clientUnit: v.totalPrice / qty };
  } else {
    input = { cost, markup };
  }
  const shipping = v.shipping ?? 0;
  const shippingMarkup = (v.shippingMarkup ?? 0) * shippingScale;
  const vendor = v.vendor || '';
  return {
    name: v.name,
    clientName: v.clientName || '',
    description: v.description || '',
    room: v.room || '',
    vendor,
    vendorName: vendor,
    sku: v.sku || '',
    imageUrl: /^https?:\/\//i.test(v.imageUrl || '') ? v.imageUrl : '',
    link: v.link || '',
    unitType: v.unitType || '',
    finishes: v.finishes || '',
    materials: v.materials || '',
    customSpecs: v.dimensions ? [{ label: 'Dimensions', value: v.dimensions }] : [],
    ...priceLine({ qty, ...input }),
    shipping,
    shippingMarkup,
    clientShipping: clientShipping(shipping, shippingMarkup),
    msrp: v.msrp ?? 0,
    category: v.category || '',
    taxable: v.taxable !== false,
    shippingTaxable: v.shippingTaxable === true,
    type: 'product',
  };
}

// Proposals from a sheet of lines. Rows are grouped by project and proposal;
// rows without a proposal go into one proposal per project, or per room when
// `groupBy` is 'room'. Declined lines, rows that only name a purchase order or
// invoice, and purchase-only rows (a cost but no selling price, in a sheet
// that has selling prices) are left out and counted.
export function sheetProposalEntries(
  sheet,
  mapping,
  { sellMode = 'withMarkup', groupBy = 'project' } = {}
) {
  const entries = sheet.dataRows.map((row) => ({
    row,
    values: readRow('proposals', row, mapping, sheet).values,
  }));
  const options = {
    sellMode,
    markupScale: percentScale(entries, 'markup'),
    shippingScale: percentScale(entries, 'shippingMarkup'),
  };
  const hasSelling = SELLING_FIELDS.some((field) => mapping.includes(field));
  const stats = { lines: 0, missingName: 0, declined: 0, notOnProposal: 0, purchaseOnly: 0 };

  const groups = new Map();
  entries.forEach(({ row, values: v }) => {
    if (!v.name) {
      stats.missingName += 1;
      return;
    }
    if (DECLINED.test(v.approval || '')) {
      stats.declined += 1;
      return;
    }
    const { key, notOnProposal } = readProposalKey(v.proposal);
    if (notOnProposal) {
      stats.notOnProposal += 1;
      return;
    }
    if (hasSelling && !(v.unitPrice > 0 || v.totalPrice) && v.unitCost > 0) {
      stats.purchaseOnly += 1;
      return;
    }
    const part = key || (groupBy === 'room' ? `room:${v.room || ''}` : '');
    const groupKey = `${norm(v.projectName)}|${norm(part)}`;
    if (!groups.has(groupKey)) groups.set(groupKey, { key, room: v.room || '', items: [] });
    groups.get(groupKey).items.push({ row, values: v });
  });

  const proposals = [...groups.values()].map((group) => {
    const rows = group.items.map((item) => item.row);
    const lines = group.items.map((item) => sheetLine(item.values, options));
    const dates = group.items
      .map((item) => item.values.date?.date)
      .filter(Boolean)
      .sort();
    const first = group.items[0].values;
    stats.lines += lines.length;
    const byNumber = looksLikeNumber(group.key);
    return {
      importRef: `${sheet.name}#${rows[0].rowNumber}:${hashText(
        rows.map((row) => row.cells.map(cellText).join('\u0001')).join('\u0002')
      )}`,
      sourceRows: rows.map((row) => row.rowNumber),
      fields: {
        proposalNumber: byNumber ? group.key : '',
        projectId: '',
        date: dates[0] || '',
        title: byNumber ? '' : group.key || (groupBy === 'room' ? group.room : ''),
        status: 'archived',
      },
      extra: {
        projectName: first.projectName || '',
        clientName: '',
        clientEmail: '',
        billingAddress: '',
        notes: '',
        createdAt: first.date?.iso || '',
        salesTaxRate: null,
        lines,
        total: null,
        sourceTotal: null,
        unpricedLines: lines.filter((l) => !l.clientUnit && l.qty).length,
        payloadBytes: payloadSize(lines),
      },
    };
  });
  return { entries: proposals, stats: { ...stats, proposals: proposals.length } };
}

/* ============================================================
   Review rows
============================================================ */

// Review rows for the proposals read from either kind of file: each linked to
// the studio's project of the same name (and client, when the file names one),
// and left unselected when the studio already has it.
export function buildProposalRows({ entries, stats }, context = {}) {
  const findProject = projectMatcher(context.projects || []);
  let existing = 0;
  const rows = entries.map((entry, index) => {
    const row = {
      id: `p-${entry.sourceRows[0]}-${index}`,
      importRef: entry.importRef,
      sourceRows: entry.sourceRows,
      archived: false,
      fields: { ...entry.fields },
      extra: entry.extra,
      flags: {},
    };
    const match = findProject(entry.extra.projectName, entry.extra.clientName);
    if (match) {
      row.fields.projectId = match.project.projectId;
      row.flags.ambiguousProject = match.ambiguous;
    }
    row.existing = existingReason('proposals', row, context);
    if (row.existing) existing += 1;
    row.selected = !row.existing;
    return row;
  });
  return {
    rows,
    stats: {
      ...stats,
      combinedRows: 0,
      missingName: stats.missingName || 0,
      existing,
      archivedUnselected: 0,
      archivedIncluded: 0,
      placeholderEmails: 0,
    },
  };
}
